import { Check } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * Shared accent palette for clients and calendars.
 *
 * Fixed swatches rather than a free picker: every value is tuned to read as a
 * small dot / event fill in both themes.
 */
export const ACCENT_COLORS: { value: string; name: string }[] = [
  { value: 'oklch(0.65 0.09 155)', name: 'Sage' },
  { value: 'oklch(0.68 0.12 195)', name: 'Teal' },
  { value: 'oklch(0.65 0.11 250)', name: 'Blue' },
  { value: 'oklch(0.62 0.13 290)', name: 'Indigo' },
  { value: 'oklch(0.65 0.14 330)', name: 'Magenta' },
  { value: 'oklch(0.64 0.16 20)', name: 'Rose' },
  { value: 'oklch(0.68 0.14 45)', name: 'Clay' },
  { value: 'oklch(0.72 0.13 80)', name: 'Amber' },
  { value: 'oklch(0.70 0.12 120)', name: 'Olive' },
  { value: 'oklch(0.62 0.02 260)', name: 'Slate' },
];

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Visible legend text. */
  label?: string;
  /** aria-label for the radiogroup. Defaults to label. */
  groupLabel?: string;
}

export function ColorPicker({
  value,
  onChange,
  label = 'Colour',
  groupLabel,
}: Props) {
  return (
    <fieldset>
      <legend className="mb-2 text-small font-medium text-text-secondary">{label}</legend>
      <div
        role="radiogroup"
        aria-label={groupLabel ?? label}
        className="flex flex-wrap gap-2"
      >
        {ACCENT_COLORS.map((swatch) => {
          const active = swatch.value === value;
          return (
            <button
              key={swatch.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={swatch.name}
              title={swatch.name}
              onClick={() => onChange(swatch.value)}
              className={cn(
                'pressable flex size-action items-center justify-center rounded-full',
                'ring-offset-2 ring-offset-[var(--bg)]',
                active && 'ring-2 ring-[var(--text)]',
              )}
              style={{ backgroundColor: swatch.value }}
            >
              {active && (
                <Check size={14} strokeWidth={3} className="text-[var(--bg)]" aria-hidden />
              )}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
